// @owners { team: patients }
import { Toast, ToastContext } from '@alto/design-system';
import { useRegisterWaitlistUser } from '@alto/features';
import React, { useContext, useEffect, useState } from 'react';
import { setFertDeliverySignup } from '~shared/actions/uiOnboarding';
import { useAnalytics } from '~shared/hooks/useAnalytics';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { useDispatchShared } from '~shared/store';
import { validateEmail } from '~shared/validations';

type UseGeoUncoveredWaitlistParams = {
  zip: string;
};

export const useGeoUncoveredWaitlist = ({ zip }: UseGeoUncoveredWaitlistParams) => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const { addToast } = useContext(ToastContext);
  const { mutateAsync } = useRegisterWaitlistUser();
  const dispatch = useDispatchShared();
  const { trackPageView, trackEvent } = useAnalytics();

  useEffect(() => {
    trackPageView({
      event: EVENTS.ONBOARDING_OOZ_ZIP_CODE,
      params: {
        zip,
      },
    });
  }, [zip, trackPageView]);

  const handleEmailChange = (text: string) => {
    setEmail(text);
    if (error && !validateEmail(text)) {
      setError('');
    }
  };

  const handleSubmit = async ({ onSuccess }: { onSuccess: () => void }) => {
    const validationError = validateEmail(email);
    if (validationError) {
      setError(validationError);
      return;
    }
    setError('');
    const response = await mutateAsync({ zip, email, first_name: null, last_name: null });
    if (response.errors) {
      addToast(<Toast variant="error">Error saving email address</Toast>);
      return;
    }
    addToast(<Toast variant="success">Email address saved</Toast>);
    onSuccess();
  };

  const handleCreateAccountFert = ({ afterPress }: { afterPress?: () => void } = {}) => {
    trackEvent({
      event: EVENTS.ONBOARDING_FERT_DELIVERY_SIGN_UP_CLICKED,
      params: {
        zip,
      },
    });
    dispatch(setFertDeliverySignup(false));
    afterPress?.();
  };

  return { email, error, handleEmailChange, handleSubmit, handleCreateAccountFert };
};
